import { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

/* ─────────── Layout blocks ─────────── */

export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
      <div className="space-y-2">
        {eyebrow && (
          <div className="text-[10px] font-mono-num uppercase tracking-[0.22em] text-neutral-500">
            {eyebrow}
          </div>
        )}
        <h1 className="font-display text-4xl xl:text-5xl font-medium tracking-tight leading-none">
          {title}
        </h1>
        {subtitle && (
          <div className="text-sm text-neutral-500 pt-1">{subtitle}</div>
        )}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Section({
  eyebrow,
  children,
  className,
}: {
  eyebrow?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("space-y-4", className)}>
      {eyebrow && (
        <div className="flex items-center gap-3">
          <span className="h-px w-6 bg-neutral-300" />
          <span className="text-[10px] font-mono-num uppercase tracking-[0.22em] text-neutral-500">
            {eyebrow}
          </span>
        </div>
      )}
      {children}
    </section>
  );
}

/* ─────────── Data primitives ─────────── */

export function Metric({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] font-mono-num uppercase tracking-[0.18em] text-neutral-500">
        {label}
      </div>
      <div className="font-display text-3xl font-medium tracking-tight tabular-nums">{value}</div>
      {hint && <div className="text-[11px] text-neutral-500 font-mono-num">{hint}</div>}
    </div>
  );
}

/* ─────────── Search input con debounce ─────────── */

export function SearchInput({
  value,
  onChange,
  placeholder = "Buscar…",
  debounceMs = 250,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  debounceMs?: number;
  className?: string;
}) {
  const [local, setLocal] = useState(value);

  useEffect(() => setLocal(value), [value]);

  useEffect(() => {
    if (local === value) return;
    const t = setTimeout(() => onChange(local), debounceMs);
    return () => clearTimeout(t);
  }, [local, value, debounceMs, onChange]);

  return (
    <div className={cn("relative", className)}>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400"
        strokeWidth={2}
      />
      <input
        type="text"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder={placeholder}
        className="h-10 w-64 rounded-md border border-neutral-200 bg-white pl-9 pr-8 text-sm placeholder:text-neutral-400 focus:outline-none focus:border-neutral-400 transition-colors"
      />
      {local && (
        <button
          type="button"
          onClick={() => setLocal("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-neutral-400 hover:text-neutral-700 transition-colors"
          aria-label="Limpiar búsqueda"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.25} />
        </button>
      )}
    </div>
  );
}

/* ─────────── Buttons ─────────── */

type BtnProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "outline" | "ghost";
  size?: "sm" | "md";
};

export function EditorialButton({
  variant = "primary",
  size = "md",
  className,
  children,
  ...rest
}: BtnProps) {
  return (
    <button
      className={cn(
        "inline-flex items-center gap-2 rounded-md font-medium tracking-tight transition-all disabled:opacity-60 disabled:cursor-not-allowed",
        size === "sm" ? "h-9 px-4 text-[13px]" : "h-11 px-5 text-[14px]",
        variant === "primary" &&
          "bg-ink text-white hover:shadow-[0_10px_24px_-10px_rgba(0,0,0,0.35)] active:translate-y-[1px]",
        variant === "outline" &&
          "border border-neutral-200 bg-white text-neutral-900 hover:bg-neutral-50 hover:border-neutral-300",
        variant === "ghost" &&
          "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100",
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
